import React from 'react';
import { TrendingUp, Lock, Phone, CheckCircle2, ShieldCheck, Zap, Globe, Heart, Target } from 'lucide-react';

export function LandingFeatures() {
  return (
    <>
      {/* Feature Section: Simplicity */}
      <section id="simplicity" className="py-20 lg:py-32 bg-white dark:bg-[#0A0A0B] relative overflow-hidden">
        {/* Subtle Background Photo for Simplicity */}
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-[0.03] dark:opacity-[0.05] pointer-events-none">
          <img 
            src="https://images.unsplash.com/photo-1556742049-04ff4361c27a?auto=format&fit=crop&q=80&w=1000" 
            className="w-full h-full object-cover" 
            alt="Person using phone"
          />
        </div>

        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-500 dark:text-blue-400 text-xs font-bold uppercase tracking-wider">
                <TrendingUp className="w-3.5 h-3.5" />
                Simplicity First
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight">Know where your money goes.</h2>
              <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                We automatically categorize everything. No manual entry, no confusing spreadsheets. Just a clear picture of your life.
              </p>
              <ul className="space-y-4 pt-4">
                {[
                  'Automatic transaction categorization',
                  'Real-time spending insights',
                  'Smart budget recommendations',
                  'One-click financial reports'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700 dark:text-slate-300 font-medium">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="lg:col-span-7 relative">
               <div className="absolute -inset-4 bg-blue-500/10 rounded-[3rem] blur-3xl" />
               <div className="relative bg-slate-50 dark:bg-white/5 rounded-[2.5rem] p-8 md:p-12 border border-slate-200 dark:border-white/10 overflow-hidden group">
                  {/* Embedded human photo in feature grid */}
                  <div className="absolute top-0 right-0 w-48 h-48 opacity-10 group-hover:opacity-20 transition-opacity">
                    <img src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&q=80&w=500" className="w-full h-full object-cover rounded-bl-[3rem]" alt="Finance management" />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 relative z-10">
                    {[
                      { label: 'Groceries', color: 'bg-green-500', icon: '🛒' },
                      { label: 'Rent', color: 'bg-blue-500', icon: '🏠' },
                      { label: 'Fun', color: 'bg-purple-500', icon: '🎬' },
                      { label: 'Transport', color: 'bg-orange-500', icon: '🚗' },
                      { label: 'Dining', color: 'bg-red-500', icon: '🍱' },
                      { label: 'Health', color: 'bg-cyan-500', icon: '💊' },
                    ].map((cat, i) => (
                      <div key={i} className="bg-white dark:bg-slate-900/80 backdrop-blur-sm p-5 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all group/item">
                        <div className="text-2xl mb-2 group-hover/item:scale-125 transition-transform duration-500">{cat.icon}</div>
                        <div className="font-bold text-sm mb-1">{cat.label}</div>
                        <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
                          <div className={`${cat.color} h-full w-[70%] rounded-full`} />
                        </div>
                      </div>
                    ))}
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Section: Peace of Mind */}
      <section id="peace-of-mind" className="py-20 lg:py-32 bg-slate-50 dark:bg-slate-900/30 relative">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">Financial Peace of Mind</h2>
            <p className="text-lg text-slate-600 dark:text-slate-400">
              Tools designed to help you breathe easier and reach your goals faster.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Goal Tracking',
                desc: 'Save for what matters. Whether it\'s a new home or a dream vacation, we help you get there.',
                icon: <Target className="w-6 h-6" />,
                color: 'text-purple-500',
                bg: 'bg-purple-500/10',
                img: 'https://images.unsplash.com/photo-1537724326059-2ea20251b9c8?auto=format&fit=crop&q=80&w=400'
              },
              {
                title: 'Smart Alerts',
                desc: 'Never miss a bill again. We notify you before payments are due and when unusual activity occurs.',
                icon: <Zap className="w-6 h-6" />,
                color: 'text-yellow-500',
                bg: 'bg-yellow-500/10',
                img: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80&w=400'
              },
              {
                title: 'Family Sharing',
                desc: 'Manage finances together. Share accounts and budgets with your partner or family securely.',
                icon: <Heart className="w-6 h-6" />,
                color: 'text-red-500',
                bg: 'bg-red-500/10',
                img: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?auto=format&fit=crop&q=80&w=400'
              }
            ].map((feature, i) => (
              <div key={i} className="group bg-white dark:bg-slate-800/50 rounded-3xl border border-slate-200 dark:border-white/10 overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-2">
                <div className="h-48 overflow-hidden relative">
                  <div className="absolute inset-0 bg-gradient-to-t from-white dark:from-slate-800 to-transparent z-10" />
                  <img src={feature.img} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt={feature.title} />
                  <div className={`absolute bottom-4 left-6 z-20 w-12 h-12 rounded-2xl ${feature.bg} ${feature.color} flex items-center justify-center shadow-lg`}>
                    {feature.icon}
                  </div>
                </div>
                <div className="p-8 pt-2">
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm">
                    {feature.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Section: Safety */}
      <section id="safety" className="py-20 lg:py-32 bg-white dark:bg-[#0A0A0B] overflow-hidden relative">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="flex-1 relative order-2 lg:order-1">
              <div className="absolute -inset-10 bg-blue-600/20 rounded-full blur-[120px] opacity-30 animate-pulse" />
              <div className="relative rounded-[3rem] overflow-hidden aspect-square max-w-md mx-auto shadow-2xl border-8 border-white dark:border-slate-800 rotate-2">
                <img 
                  src="https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&q=80&w=800" 
                  className="w-full h-full object-cover" 
                  alt="Confident person"
                />
                <div className="absolute inset-0 bg-blue-600/10" />
                <div className="absolute bottom-8 left-8 right-8 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl p-6 rounded-2xl border border-white/20 shadow-2xl">
                   <div className="flex items-center gap-4">
                     <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                       <ShieldCheck className="w-5 h-5 text-white" />
                     </div>
                     <p className="font-bold text-sm tracking-tight">Verified Secure by SSL Gold Standards</p>
                   </div>
                </div>
              </div>
            </div>
            
            <div className="flex-1 space-y-8 order-1 lg:order-2">
              <div className="space-y-4">
                <h2 className="text-4xl md:text-5xl font-bold tracking-tight">Your safety is our <span className="text-blue-500">obsession.</span></h2>
                <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                  We use the same encryption standards as major banks. Your data is yours alone, and we never sell your personal information.
                </p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { title: 'Encrypted Data', icon: <Lock className="w-5 h-5" /> },
                  { title: '24/7 Monitoring', icon: <ShieldCheck className="w-5 h-5" /> },
                  { title: 'Privacy First', icon: <Globe className="w-5 h-5" /> },
                  { title: 'Biometric Login', icon: <Phone className="w-5 h-5" /> },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-5 rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 hover:border-blue-500/30 transition-colors">
                    <div className="text-blue-500">{item.icon}</div>
                    <span className="font-bold">{item.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 lg:py-40 bg-blue-600 dark:bg-blue-700 relative overflow-hidden">
        {/* Human Background for CTA */}
        <div className="absolute inset-0 opacity-20 mix-blend-overlay">
          <img src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&q=80&w=2000" className="w-full h-full object-cover" alt="Successful person" />
        </div>
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:40px_40px]" />
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent to-black/30" />
        
        <div className="container mx-auto px-6 relative z-10 text-center">
          <div className="max-w-4xl mx-auto space-y-10">
            <h2 className="text-5xl md:text-7xl font-bold text-white tracking-tighter leading-[0.9]">
              Master your money, <br />
              <span className="text-blue-200">the human way.</span>
            </h2>
            <p className="text-xl md:text-2xl text-blue-100 font-light max-w-2xl mx-auto leading-relaxed">
              Join 10,000+ people who have already simplified their financial lives with KwachaLite.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-4">
              <button className="w-full sm:w-auto px-12 py-6 bg-white text-blue-600 font-bold rounded-full hover:bg-blue-50 transition-all shadow-2xl hover:shadow-blue-400/20 hover:scale-105 active:scale-95 text-lg">
                Open Your Free Account
              </button>
              <button className="w-full sm:w-auto px-12 py-6 bg-blue-500/20 text-white font-bold rounded-full border border-white/20 hover:bg-white/10 transition-all text-lg backdrop-blur-md">
                Schedule a Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="py-12 bg-white dark:bg-[#0A0A0B] border-t border-slate-200 dark:border-white/5">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="text-slate-500 dark:text-white/40 text-sm font-bold tracking-widest uppercase">
              © 2026 KwachaLite Finance.
            </div>
            <div className="flex items-center gap-10">
              {['Privacy', 'Terms', 'Help', 'Contact'].map(item => (
                <a key={item} href="#" className="text-slate-500 dark:text-white/40 hover:text-blue-500 dark:hover:text-blue-400 text-xs font-bold uppercase tracking-widest transition-colors">
                  {item}
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}